<template>
  <div class="app-customer-profile">
    <ChangePasswordInput
      ref="changePassword"
      :show.sync = showPwChangeModal>
      <template v-for="slot in possibleChildSlotsFor('ChangePasswordInput')" v-slot:[`${slot.child}`]>
        <slot :name="`${slot.parent}`"></slot>
      </template>
    </ChangePasswordInput>

    <div class="data">
      <div class="card-wrapper">
        <div class="card customer-data">
          <slot name="customerDataTitle"></slot>
          <PersonalData :personalData="personalData" v-if="!showError1">
            <template #beforePersonalData></template>
            <template #labelSalutation><slot name="lblSalutationWrapper"></slot></template>
            <template #labelName><slot name="contractHolderName"></slot></template>
            <template #labelAlternativePhone><slot name="alternativePhone"></slot></template>
            <template #labelStreetNumber><slot name="streetAndNumber"></slot></template>
            <template #labelZipCity><slot name="zipCodeAndCity"></slot></template>
            <template #labelSupplement><slot name="addressSupplement"></slot></template>
            <template #labelBirthDate><slot name="birthDate"></slot></template>
            <template #labelEmail><slot name="emailAddress"></slot></template>
            <template #labelPhone><slot name="labelPhone"></slot></template>
          </PersonalData>
          <div v-if="showError1" v-html="this.errorMsg1" class="error-box"> </div>
        </div>
      </div>

      <div class="card-wrapper">
        <div class="card payment">
          <h3><slot name="customerPaymentTitle"></slot></h3>
          <div class="no-payment" v-if="isNoPayment">
            <slot name="txtNoPayment">Keine Zahlart hinterlegt</slot>
          </div>
          <PaymentInformation
            :payment="paymentInformation.paymentType" v-if="!showError2">
            <template #txtBefore><slot name="txtBeforeBankAccount"></slot></template>
            <template #lblOwnerBankAccound><slot name="accountHolder"></slot></template>
            <template #lblIbanBankAccount><slot name="iban"></slot></template>
          </PaymentInformation>
          <div class="row" v-if="isBankAccount">
            <div class="label"><slot name="paymentMethod"></slot></div>
            <div class="value">
              <slot :name="`paymentBankAccount`">Lastschrift</slot>
            </div>
          </div>
          <div v-if="showError2" v-html="this.errorMsg2" class="error-box"></div>
        </div>
      </div>

      <div class="card-wrapper">
        <div class="card saved-data">
          <div class="content">
            <div class="text">
              <slot name="customerSavedData"></slot>
              <div><slot name="infoText"></slot></div>
              <LoginConfirm ref="doLoginConfirmRef">
                <template #txtInvalidLoginConfirmPassword><slot name="txtInvalidLoginConfirmPassword"></slot></template>
              </LoginConfirm>
            </div>
            <div class="download-button">
              <button class="button-border" type="submit" @click="verifyUser" :disabled="loading">
                <slot name="ctaDownload"></slot>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="card-wrapper">
        <a class="card card-link-only" :href="selfserviceConsentLink">
          <slot name="ctaConsent"></slot>
        </a>
      </div>

      <div class="card-wrapper">
        <a class="card password card-edit-only" @click="doPwChange">
          <slot name="passwordTitle"></slot>
        </a>
      </div>

      <div class="card-wrapper">
        <div class="card logout">
          <button @click="doLogout" :disabled="loggingOut"><slot name="ctaLogOut"></slot></button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import PaymentInformation from '@/components/process/generic/Payment/flash/PaymentInformation.vue'
import PersonalData from '@/components/process/generic/PersonalData/PersonalData.vue'
import LoginConfirm from '@/components/process/generic/Login/LoginConfirm.vue'
import doLoginConfirm from '@/mixins/doLoginConfirm'
import ChangePasswordInput from '@/components/process/generic/ChangePassword/Input.vue'
import { wrapQuery } from '@/helper/wrapGQLQuery'
import gql from 'graphql-tag'
import transaction from '@/mixins/transaction'
import logout from '@/mixins/logout'
import { mapMutations } from 'vuex'
import { splitError } from '@/helper/splitError'

import FileSaver from 'file-saver'
import appFileSaver from '@/mixins/appFileSaver'

export default {
  name: 'CustomerProfile',
  mixins: [
    transaction('personalData'),
    doLoginConfirm('doLoginConfirmRef'),
    logout,

    appFileSaver
  ],
  components: {
    PaymentInformation,
    PersonalData,
    LoginConfirm,
    ChangePasswordInput
  },
  data () {
    return {
      loadedCurrentCustomer: false,
      loadedPaymentInformation: false,
      loading: false,
      loggingOut: false,
      currentCustomer: {
        salutation: '',
        firstName: '',
        lastName: '',
        birthDate: '',
        address: {
          city: '',
          houseNumber: '',
          street: '',
          supplement: '',
          zip: ''
        },
        alternativePhone: '',
        email: '',
        emailVerified: false
      },
      currentEvnConfiguration: {
        evn: ''
      },
      paymentInformation: {
        paymentType: null
      },
      customerDataSheet: '',
      verificationMailSent: false,
      showPwChangeModal: false,
      showError1: false,
      showError2: false,
      errorMsg1: {
      },
      errorMsg2: {
      }
    }
  },
  props: {
    selfserviceConsentLink: {
      type: String,
      default: '#'
    },
    logoutRedirectTo: {
      type: String,
      default: null
    }
  },
  mounted () {
    this.startLoader()
    this.resetLoginConfirm()
  },
  computed: {
    isNoPayment () {
      return this.paymentInformation.paymentType === null
    },
    isBankAccount () {
      if (this.paymentInformation.paymentType !== null) {
        return this.paymentInformation.paymentType.type === 'bankAccount'
      }
      return false
    },
    personalData: function () {
      return {
        person: {
          firstName: this.currentCustomer.firstName,
          lastName: this.currentCustomer.lastName,
          birthDate: this.currentCustomer.birthDate
        },
        address: this.currentCustomer.address,
        additional: {
          email: this.currentCustomer.email,
          emailVerified: this.currentCustomer.emailVerified,
          gdprCSV: this.currentCustomer.gdprCSV
        }
      }
    },
    inWebView () {
      return window.App !== undefined && window.App.reset !== undefined && window.App.refreshedToken !== undefined
    },
    isIosDevice () {
      return [
        'iPad Simulator',
        'iPhone Simulator',
        'iPod Simulator',
        'iPad',
        'iPhone',
        'iPod'
      ].includes(navigator.platform) ||
        // iPad on iOS 13 detection
        (navigator.userAgent.includes('Mac') && 'ontouchend' in document)
    }
  },
  methods: {
    ...mapMutations('forgotPassword', [
      'captureMsisdn'
    ]),
    verifyUser () {
      const _this = this
      this.resetLoginConfirm()
      this.doLoginConfirm().then(() => {
        _this.downloadSheet()
      }).catch((e) => {
      })
    },
    downloadSheet () {
      // Tracking all over the place :-)
      const data = {
        file_name: 'kundendaten.csv'
      }
      if (typeof window.trackingNS?.track?.tracking === 'function') {
        window.trackingNS.track.tracking('download file', data)
      }
      const file = new Blob([this.personalData.additional.gdprCSV], { type: 'text/csv' })
      if (this.appTriggerSaveAsFileAvailable()) {
        this.appTriggerSaveAsFile('kundendaten.csv', file, false)
      } else {
        FileSaver.saveAs(file, 'kundendaten.csv')
      }
    },
    doLogout () {
      this.loggingOut = true
      if (this.inWebView) {
        if (this.isIosDevice) {
          window.webkit.messageHandlers.reset.postMessage('logging out')
        } else {
          window.App.reset('logging out')
        }
      }
      this.logOut(this.logoutRedirectTo)
    },
    doPwChange () {
      this.$refs.changePassword.resetInput()
      this.showPwChangeModal = true
    },
    checkAndDisableLoader () {
      if (this.loadedCurrentCustomer && this.loadedPaymentInformation) {
        this.stopLoader()
      }
    }
  },
  apollo: {
    currentCustomer: function () {
      return wrapQuery(
        gql`query personalData{
          currentCustomer {
            salutation,
            firstName,
            birthDate,
            lastName,
            address {
              city,
              houseNumber,
              street,
              supplement,
              zip
            },
            alternativePhone,
            email,
            emailVerified,
            gdprCSV

          }
        }`, this, 'currentCustomer', {}, () => {
          this.showError1 = false
          this.errorMsg1 = ''
          this.loadedCurrentCustomer = true
          this.checkAndDisableLoader()
        },
        { fetchPolicy: 'no-cache' },
        (error) => {
          splitError(error).forEach(e => {
            this.errorMsg1 = e.message
          })
          if (!this.showError1) {
            const data = {
              type: 'technical',
              message: this.errorMsg1
            }
            if (typeof window.trackingNS?.track?.tracking === 'function') {
              window.trackingNS.track.tracking('error', data)
            }
          }
          this.showError1 = true

          this.loadedCurrentCustomer = true
          this.checkAndDisableLoader()
        }
      )
    },
    paymentInformation: function () {
      return wrapQuery(
        gql`query personalData {
          paymentInformation {
            paymentType {
            ...on BankAccount {
                type
                iban
                owner
              }
              ...on CreditCard {
                type
                holder
                company
                expiryMonth
                expiryYear
                lastDigits
              }
           }
          }
        }`, this, 'paymentInformation', {}, () => {
          this.showError2 = false
          this.errorMsg2 = ''
          this.loadedPaymentInformation = true
          this.checkAndDisableLoader()
        },
        {},
        (error) => {
          splitError(error).forEach(e => {
            this.errorMsg2 = e.message
          })
          if (!this.showError2) {
            const data = {
              type: 'technical',
              message: this.errorMsg2
            }
            if (typeof window.trackingNS?.track?.tracking === 'function') {
              window.trackingNS.track.tracking('error', data)
            }
          }
          this.showError2 = true

          this.loadedCurrentCustomer = true
          this.checkAndDisableLoader()
        }
      )
    }
  }
}
</script>
